/**
 * Backend type tag (serialization only). Tells the backend how to parse the
 * value. UI behavior is driven by `IFilterableColumn.kind`, not this.
 */
export enum FilterType {
  String = 'String',
  Number = 'Number',
  Date = 'Date',
  GeoBounds = 'GeoBounds',
  Boolean = 'Boolean',
  Lookup = 'Lookup',
}

/**
 * UI shape of a filter input. Explicit so the renderer is a pure `switch`
 * rather than a series of "has options? has toggleOptions?" heuristics.
 */
export type FilterKind =
  | 'text'
  | 'number'
  | 'date'
  | 'select'
  | 'multi-select'
  | 'toggle';

/**
 * Backend operation field - matches backend Operations enum
 */
export enum FilterOperator {
  /** Equals */
  Eq = 'Eq',
  /** Not equals */
  NEq = 'NEq',
  /** Greater than */
  Gt = 'Gt',
  /** Less than */
  Lt = 'Lt',
  /** Greater than or equal */
  GtE = 'GtE',
  /** Less than or equal */
  LtE = 'LtE',
  /** Begins with */
  BgnW = 'BgnW',
  /** Ends with */
  EndW = 'EndW',
  /** Not begins with */
  NBgnW = 'NBgnW',
  /** Not ends with */
  NEndW = 'NEndW',
  /** Contains */
  Cnt = 'Cnt',
  /** Not contains */
  NCnt = 'NCnt',
  /** Not null */
  NNul = 'NNul',
  /** In */
  In = 'In',
  /** Not in */
  NIn = 'NIn',
}

/**
 * Column that can be filtered.
 *
 * `type` is the backend serialization tag. `kind` is the UI shape and is the
 * single discriminant the renderer reads. Builders (see `metadataFilter.ts`)
 * are responsible for setting both consistently.
 */
export interface IFilterableColumn {
  /** Alias/key sent to backend (e.g., "id", "code") */
  key: string;
  /** UI display label (e.g., "ID", "Code") */
  label: string;
  /** Backend serialization tag */
  type: FilterType;
  /** UI shape (omit only for legacy callers; inferred to 'text' / 'number' /
   *  'date' from `type` when missing). */
  kind?: FilterKind;
  /** Override default operators for this column */
  operators?: FilterOperator[];
  /** Options for select / multi-select / toggle kinds */
  options?: IFilterOption[];
  /** Two-state toggle options (`[onOption, offOption]`). Required when
   *  `kind === 'toggle'`. */
  toggleOptions?: [IFilterOption, IFilterOption];
  /** When true, renders as a default filter chip that cannot be removed,
   *  only reset (value cleared so it stops being sent to the backend). */
  isDefault?: boolean;
}

/**
 * Applied filter state
 * For In operator, value is string[] (array of values)
 * For other operators, value is string
 */
export interface IActiveFilter {
  column: IFilterableColumn;
  operator: FilterOperator;
  value: string | string[];
}

/**
 * Filter structure sent to backend
 */
export interface IFilterPayload {
  column: string;
  operation: FilterOperator | string;
  value: string;
  type: FilterType | string;
}

/**
 * Configuration for a filter operator
 */
export interface IOperatorConfig {
  label: string;
  requiresValue: boolean;
}

/**
 * Option for select/multi-select filter inputs
 */
export interface IFilterOption {
  value: string;
  label?: string;
}

/**
 * Column filter configuration (used in PagedDataGrid column definition)
 */
export interface IColumnFilterConfig {
  enabled: boolean;
  type: FilterType;
  /** Override default operators for this column */
  operators?: FilterOperator[];
  options?: IFilterOption[];
  /** When provided (exactly 2 items: [onOption, offOption]), renders as a toggle switch
   *  instead of the standard filter form. Other filter fields (operators, options) are ignored. */
  toggleOptions?: [IFilterOption, IFilterOption];
}
